import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Users, Package, FileText, FileSignature, Settings as Cog,
  Lock, Loader2, Plus, Trash2, Edit3, Send, Check, Download,
  X, Search, Euro, ExternalLink, Wrench, CalendarPlus,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SECRET_KEY = "computheer_admin_secret";

import RepairsTab from "./RepairsTab";
import AdminAppointmentDialog from "./AdminAppointmentDialog";

// ===== utilities =====
const fmtEur = (n) =>
  new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(Number(n || 0));

const statusBadge = (s) => {
  const map = {
    draft:    { bg: "bg-neutral-200", fg: "text-neutral-800", label: "Concept" },
    sent:     { bg: "bg-blue-100",    fg: "text-blue-800",    label: "Verzonden" },
    paid:     { bg: "bg-emerald-100", fg: "text-emerald-800", label: "Betaald" },
    accepted: { bg: "bg-emerald-100", fg: "text-emerald-800", label: "Akkoord" },
    declined: { bg: "bg-rose-100",    fg: "text-rose-800",    label: "Geweigerd" },
    canceled: { bg: "bg-neutral-200", fg: "text-neutral-700", label: "Geannuleerd" },
  };
  const m = map[s] || map.draft;
  return (
    <span className={`px-2 py-0.5 text-[11px] font-semibold tracking-wide uppercase ${m.bg} ${m.fg}`}>
      {m.label}
    </span>
  );
};

// ===== shell =====
const AdminCRM = () => {
  const [secret, setSecret] = useState(() => localStorage.getItem(SECRET_KEY) || "");
  const [secretInput, setSecretInput] = useState("");
  const [tab, setTab] = useState("customers");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!secretInput.trim()) return;
    localStorage.setItem(SECRET_KEY, secretInput.trim());
    setSecret(secretInput.trim());
  };

  if (!secret) {
    return (
      <section className="min-h-[60vh] py-20 bg-neutral-50">
        <div className="max-w-md mx-auto px-5 md:px-10">
          <div className="bg-white border border-neutral-200 p-8">
            <div className="w-12 h-12 bg-neutral-900 text-white flex items-center justify-center mb-5">
              <Lock size={20} />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-2">CRM toegang</h1>
            <p className="text-neutral-600 text-sm mb-6">Voer je admin-sleutel in om toegang te krijgen tot het CRM.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="password"
                value={secretInput}
                onChange={(e) => setSecretInput(e.target.value)}
                placeholder="Admin-sleutel"
                autoFocus
                className="w-full border border-neutral-300 px-4 py-3 outline-none focus:border-neutral-900 text-sm"
                data-testid="crm-secret-input"
              />
              <button
                type="submit"
                className="w-full bg-neutral-900 hover:bg-black text-white px-5 py-3 text-sm font-medium"
                data-testid="crm-secret-submit"
              >Toegang</button>
            </form>
          </div>
        </div>
      </section>
    );
  }

  const tabs = [
    { k: "customers", label: "Klanten",     icon: <Users size={15} /> },
    { k: "repairs",   label: "Reparaties",  icon: <Wrench size={15} /> },
    { k: "products",  label: "Producten",   icon: <Package size={15} /> },
    { k: "invoices",  label: "Facturen",    icon: <FileText size={15} /> },
    { k: "quotes",    label: "Offertes",    icon: <FileSignature size={15} /> },
    { k: "settings",  label: "Instellingen", icon: <Cog size={15} /> },
  ];

  return (
    <section className="min-h-[80vh] py-14 bg-neutral-50">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-1">Admin</p>
            <h1 className="text-3xl font-bold text-neutral-900">CRM & Facturatie</h1>
          </div>
          <div className="flex gap-4 text-sm">
            <Link to="/admin/stats" className="text-neutral-600 hover:text-neutral-900 underline">Statistieken</Link>
            <Link to="/admin/connect" className="text-neutral-600 hover:text-neutral-900 underline">Calendar</Link>
          </div>
        </div>

        <div className="bg-white border border-neutral-200 mb-6">
          <div className="flex border-b border-neutral-200 overflow-x-auto">
            {tabs.map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`crm-tab-${t.k}`}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  tab === t.k
                    ? "border-neutral-900 text-neutral-900"
                    : "border-transparent text-neutral-500 hover:text-neutral-900"
                }`}
              >
                {t.icon}{t.label}
              </button>
            ))}
          </div>
          <div className="p-6">
            {tab === "customers" && <CustomersTab secret={secret} />}
            {tab === "repairs"   && <RepairsTab   secret={secret} />}
            {tab === "products"  && <ProductsTab  secret={secret} />}
            {tab === "invoices"  && <InvoicesTab  secret={secret} docType="invoice" />}
            {tab === "quotes"    && <InvoicesTab  secret={secret} docType="quote" />}
            {tab === "settings"  && <SettingsTab  secret={secret} />}
          </div>
        </div>
      </div>
    </section>
  );
};

// ===== Customers =====
const emptyCustomer = {
  name: "", email: "", phone: "",
  address_street: "", address_zip: "", address_city: "",
  is_business: false, company_name: "",
  has_subscription: false, subscription_type: "",
  subscription_hours_total: 0, subscription_hours_used: 0, subscription_price_monthly: 0,
  notes: "",
};

const CustomersTab = ({ secret }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // customer object or "new" or null
  const [bookingFor, setBookingFor] = useState(null); // customer to plan appointment for

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/customers`, { params: { secret, q: q || undefined } });
      setList(r.data);
    } finally { setLoading(false); }
  }, [secret, q]);

  useEffect(() => { load(); }, [load]);

  const save = async (c) => {
    // Strip empty strings so optional fields pass backend validation
    const clean = Object.fromEntries(
      Object.entries(c).filter(([, v]) => v !== "" && v !== null && v !== undefined)
    );
    try {
      if (c.id) {
        await axios.put(`${API}/admin/customers/${c.id}`, clean, { params: { secret } });
      } else {
        await axios.post(`${API}/admin/customers`, clean, { params: { secret } });
      }
      setEditing(null); load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : Array.isArray(detail) ? detail.map((d) => d.msg).join(", ")
        : e.message;
      alert("Opslaan mislukt: " + msg);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Klant definitief verwijderen?")) return;
    await axios.delete(`${API}/admin/customers/${id}`, { params: { secret } });
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Zoek op naam, e-mail, bedrijf..."
            className="w-full border border-neutral-300 pl-9 pr-3 py-2 text-sm outline-none focus:border-neutral-900"
            data-testid="customer-search"
          />
        </div>
        <button
          onClick={() => setEditing({ ...emptyCustomer })}
          className="flex items-center gap-2 bg-neutral-900 text-white px-4 py-2 text-sm font-medium hover:bg-black"
          data-testid="customer-new-btn"
        ><Plus size={14}/>Nieuwe klant</button>
      </div>

      {loading ? <Spinner/> : list.length === 0 ? (
        <EmptyState text="Nog geen klanten."/>
      ) : (
        <div className="border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3">Naam</th>
                <th className="px-4 py-3">E-mail</th>
                <th className="px-4 py-3">Telefoon</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Abonnement</th>
                <th className="px-4 py-3 text-right">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {list.map((c) => (
                <tr key={c.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3 font-medium">
                    {c.company_name ? `${c.company_name} — ${c.name}` : c.name}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">{c.email || "-"}</td>
                  <td className="px-4 py-3 text-neutral-600">{c.phone || "-"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-[11px] ${c.is_business ? "bg-indigo-100 text-indigo-800" : "bg-neutral-100 text-neutral-700"}`}>
                      {c.is_business ? "Zakelijk" : "Particulier"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-neutral-600">
                    {c.has_subscription
                      ? `${Number(c.subscription_hours_used||0).toFixed(1)} / ${Number(c.subscription_hours_total||0)} uur`
                      : "-"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setBookingFor(c)} className="text-emerald-700 hover:text-emerald-900 mr-3" title="Afspraak inplannen" data-testid={`customer-book-${c.id}`}><CalendarPlus size={14}/></button>
                    <button onClick={() => setEditing(c)} className="text-neutral-600 hover:text-neutral-900 mr-3" title="Bewerken" data-testid={`customer-edit-${c.id}`}><Edit3 size={14}/></button>
                    <button onClick={() => remove(c.id)} className="text-neutral-600 hover:text-rose-700" title="Verwijderen" data-testid={`customer-delete-${c.id}`}><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <CustomerDialog
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={save}
          onPlanAppointment={(cust) => { setEditing(null); setBookingFor(cust); }}
          secret={secret}
        />
      )}

      {bookingFor && (
        <AdminAppointmentDialog
          customer={bookingFor}
          secret={secret}
          onClose={() => setBookingFor(null)}
          onCreated={() => { /* could reload appt history */ }}
        />
      )}
    </div>
  );
};

const CustomerDialog = ({ initial, onClose, onSave, onPlanAppointment, secret }) => {
  const [c, setC] = useState(initial);
  const [appts, setAppts] = useState([]);
  const [loadingAppts, setLoadingAppts] = useState(false);
  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));

  // Load appointment history when editing existing customer
  useEffect(() => {
    if (!c.id) return;
    setLoadingAppts(true);
    axios.get(`${API}/admin/customers/${c.id}/appointments`, { params: { secret } })
      .then((r) => setAppts(r.data))
      .catch(() => setAppts([]))
      .finally(() => setLoadingAppts(false));
  }, [c.id, secret]);

  const submit = (e) => {
    e.preventDefault();
    onSave(c);
  };

  return (
    <Modal onClose={onClose} title={c.id ? "Klant bewerken" : "Nieuwe klant"}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Naam" required value={c.name} onChange={(v) => set("name", v)} testId="cust-name"/>
          <Field label="E-mail" type="email" value={c.email || ""} onChange={(v) => set("email", v)} testId="cust-email"/>
          <Field label="Telefoon" value={c.phone || ""} onChange={(v) => set("phone", v)} testId="cust-phone"/>
          <label className="flex items-end gap-2 text-sm pb-2">
            <input type="checkbox" checked={c.is_business} onChange={(e) => set("is_business", e.target.checked)} data-testid="cust-business"/>
            <span>Zakelijke klant</span>
          </label>
        </div>
        {c.is_business && (
          <Field label="Bedrijfsnaam" value={c.company_name || ""} onChange={(v) => set("company_name", v)}/>
        )}
        <div className="grid grid-cols-[2fr_1fr_2fr] gap-3">
          <Field label="Straat + nr" value={c.address_street || ""} onChange={(v) => set("address_street", v)}/>
          <Field label="Postcode" value={c.address_zip || ""} onChange={(v) => set("address_zip", v)}/>
          <Field label="Plaats" value={c.address_city || ""} onChange={(v) => set("address_city", v)}/>
        </div>

        <div className="border-t pt-4">
          <label className="flex items-center gap-2 text-sm mb-3">
            <input type="checkbox" checked={c.has_subscription} onChange={(e) => set("has_subscription", e.target.checked)} data-testid="cust-has-subscription"/>
            <span className="font-medium">Heeft support-abonnement</span>
          </label>
          {c.has_subscription && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Type" value={c.subscription_type || ""} onChange={(v) => set("subscription_type", v)} placeholder="Zakelijk 6u/jaar"/>
              <Field label="Prijs p/maand (€)" type="number" step="0.01" value={c.subscription_price_monthly || 0} onChange={(v) => set("subscription_price_monthly", parseFloat(v) || 0)}/>
              <Field label="Totaal uren" type="number" step="0.5" value={c.subscription_hours_total || 0} onChange={(v) => set("subscription_hours_total", parseFloat(v) || 0)}/>
              <Field label="Verbruikt" type="number" step="0.25" value={c.subscription_hours_used || 0} onChange={(v) => set("subscription_hours_used", parseFloat(v) || 0)}/>
            </div>
          )}
        </div>

        <div>
          <label className="text-xs text-neutral-600 uppercase tracking-wider">Notities</label>
          <textarea
            value={c.notes || ""} onChange={(e) => set("notes", e.target.value)}
            rows={2} className="mt-1 w-full border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-900"
          />
        </div>

        <div className="flex justify-between items-center gap-3 pt-2">
          {c.id && onPlanAppointment ? (
            <button type="button" onClick={() => onPlanAppointment(c)}
              className="flex items-center gap-2 text-sm text-emerald-700 hover:text-emerald-900 border border-emerald-700 px-3 py-1.5"
              data-testid="cust-plan-appointment">
              <span>📅</span> Afspraak inplannen
            </button>
          ) : <span/>}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-900">Annuleren</button>
            <button type="submit" className="px-5 py-2 bg-neutral-900 text-white text-sm font-medium hover:bg-black" data-testid="cust-save">Opslaan</button>
          </div>
        </div>
      </form>

      {c.id && (
        <div className="mt-6 border-t border-neutral-200 pt-5">
          <h3 className="text-sm font-bold text-neutral-900 uppercase tracking-wider mb-3">Geschiedenis afspraken</h3>
          {loadingAppts ? (
            <p className="text-xs text-neutral-500">Laden...</p>
          ) : appts.length === 0 ? (
            <p className="text-xs text-neutral-500">Nog geen afspraken voor deze klant.</p>
          ) : (
            <ul className="text-sm divide-y divide-neutral-100 border border-neutral-200">
              {appts.slice(0, 8).map((a) => (
                <li key={a.id} className="px-3 py-2 flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="font-medium">{a.date} · {a.time} <span className="text-xs text-neutral-500">({a.duration_minutes} min)</span></div>
                    <div className="text-xs text-neutral-500">{a.service_type} · {a.location}</div>
                  </div>
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${
                    a.status === "confirmed" ? "bg-emerald-100 text-emerald-800" :
                    a.status === "rejected" ? "bg-rose-100 text-rose-800" :
                    "bg-neutral-100 text-neutral-700"
                  }`}>{a.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
};

// ===== Products =====
const emptyProduct = { name: "", description: "", unit_price: 0, vat_rate: 21, unit: "stuk", active: true };

const ProductsTab = ({ secret }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/products`, { params: { secret } });
      setList(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async (p) => {
    if (p.id) await axios.put(`${API}/admin/products/${p.id}`, p, { params: { secret } });
    else await axios.post(`${API}/admin/products`, p, { params: { secret } });
    setEditing(null); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Product verwijderen?")) return;
    await axios.delete(`${API}/admin/products/${id}`, { params: { secret } });
    load();
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => setEditing({ ...emptyProduct })} className="flex items-center gap-2 bg-neutral-900 text-white px-4 py-2 text-sm font-medium hover:bg-black" data-testid="product-new-btn">
          <Plus size={14}/>Nieuw product / dienst
        </button>
      </div>
      {loading ? <Spinner/> : list.length === 0 ? <EmptyState text="Nog geen producten/diensten."/> : (
        <div className="border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3">Naam</th>
                <th className="px-4 py-3">Eenheid</th>
                <th className="px-4 py-3 text-right">Prijs</th>
                <th className="px-4 py-3 text-right">BTW</th>
                <th className="px-4 py-3 text-right">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {list.map((p) => (
                <tr key={p.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3">
                    <div className="font-medium">{p.name}</div>
                    {p.description && <div className="text-xs text-neutral-500 mt-0.5">{p.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">{p.unit}</td>
                  <td className="px-4 py-3 text-right font-medium">{fmtEur(p.unit_price)}</td>
                  <td className="px-4 py-3 text-right text-neutral-600">{p.vat_rate}%</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEditing(p)} className="text-neutral-600 hover:text-neutral-900 mr-3" data-testid={`product-edit-${p.id}`}><Edit3 size={14}/></button>
                    <button onClick={() => remove(p.id)} className="text-neutral-600 hover:text-rose-700" data-testid={`product-delete-${p.id}`}><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <ProductDialog initial={editing} onClose={() => setEditing(null)} onSave={save}/>}
    </div>
  );
};

const ProductDialog = ({ initial, onClose, onSave }) => {
  const [p, setP] = useState(initial);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  return (
    <Modal onClose={onClose} title={p.id ? "Product bewerken" : "Nieuw product / dienst"}>
      <form onSubmit={(e) => { e.preventDefault(); onSave(p); }} className="space-y-4">
        <Field label="Naam" required value={p.name} onChange={(v) => set("name", v)} testId="prod-name"/>
        <div>
          <label className="text-xs text-neutral-600 uppercase tracking-wider">Omschrijving</label>
          <textarea value={p.description || ""} onChange={(e) => set("description", e.target.value)} rows={2} className="mt-1 w-full border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-900"/>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Prijs (€)" type="number" step="0.01" value={p.unit_price} onChange={(v) => set("unit_price", parseFloat(v) || 0)} testId="prod-price"/>
          <Field label="BTW %" type="number" step="0.01" value={p.vat_rate} onChange={(v) => set("vat_rate", parseFloat(v) || 0)}/>
          <div>
            <label className="text-xs text-neutral-600 uppercase tracking-wider">Eenheid</label>
            <select value={p.unit} onChange={(e) => set("unit", e.target.value)} className="mt-1 w-full border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-900">
              <option value="stuk">stuk</option>
              <option value="uur">uur</option>
              <option value="dag">dag</option>
              <option value="maand">maand</option>
              <option value="jaar">jaar</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-900">Annuleren</button>
          <button type="submit" className="px-5 py-2 bg-neutral-900 text-white text-sm font-medium hover:bg-black" data-testid="prod-save">Opslaan</button>
        </div>
      </form>
    </Modal>
  );
};

// ===== Invoices / Quotes =====
const todayISO = () => new Date().toISOString().slice(0, 10);
const emptyInvoice = (type) => ({
  type,
  customer_id: "",
  date: todayISO(),
  due_date: "",
  lines: [{ description: "", quantity: 1, unit_price: 0, vat_rate: 21 }],
  notes: "",
  status: "draft",
});

const InvoicesTab = ({ secret, docType }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // full invoice or {customer_id,...} or null
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [inv, cust, prod] = await Promise.all([
        axios.get(`${API}/admin/invoices`, { params: { secret, type: docType } }),
        axios.get(`${API}/admin/customers`, { params: { secret } }),
        axios.get(`${API}/admin/products`, { params: { secret } }),
      ]);
      setList(inv.data); setCustomers(cust.data); setProducts(prod.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [docType]); // eslint-disable-line

  const save = async (inv) => {
    let id = inv.id;
    if (id) {
      await axios.put(`${API}/admin/invoices/${id}`, inv, { params: { secret } });
    } else {
      const r = await axios.post(`${API}/admin/invoices`, inv, { params: { secret } });
      id = r.data.id;
    }
    setEditing(null); await load();
  };

  const remove = async (id) => {
    if (!window.confirm("Definitief verwijderen?")) return;
    await axios.delete(`${API}/admin/invoices/${id}`, { params: { secret } });
    load();
  };

  const send = async (id) => {
    if (!window.confirm(`${docType === "quote" ? "Offerte" : "Factuur"} per e-mail versturen?`)) return;
    try {
      await axios.post(`${API}/admin/invoices/${id}/send`, {}, { params: { secret } });
      alert("Verzonden ✓");
      load();
    } catch (e) {
      alert("Fout bij versturen: " + (e.response?.data?.detail || e.message));
    }
  };

  const markPaid = async (id) => {
    await axios.post(`${API}/admin/invoices/${id}/mark-paid`, {}, { params: { secret } });
    load();
  };

  const pdfUrl = (id) => `${API}/admin/invoices/${id}/pdf?secret=${encodeURIComponent(secret)}`;

  const label = docType === "quote" ? "offerte" : "factuur";
  const LabelCap = docType === "quote" ? "Offerte" : "Factuur";

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-neutral-500">{list.length} {label}{list.length === 1 ? "" : "s"}</p>
        <button
          onClick={() => setEditing(emptyInvoice(docType))}
          className="flex items-center gap-2 bg-neutral-900 text-white px-4 py-2 text-sm font-medium hover:bg-black"
          data-testid={`${docType}-new-btn`}
        ><Plus size={14}/>Nieuwe {label}</button>
      </div>

      {loading ? <Spinner/> : list.length === 0 ? <EmptyState text={`Nog geen ${label}s.`}/> : (
        <div className="border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3">Nr.</th>
                <th className="px-4 py-3">Datum</th>
                <th className="px-4 py-3">Klant</th>
                <th className="px-4 py-3 text-right">Totaal</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {list.map((inv) => (
                <tr key={inv.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3 font-mono text-xs">{inv.number}</td>
                  <td className="px-4 py-3 text-neutral-600">{inv.date}</td>
                  <td className="px-4 py-3">{inv.customer_name}</td>
                  <td className="px-4 py-3 text-right font-medium">{fmtEur(inv.total)}</td>
                  <td className="px-4 py-3">{statusBadge(inv.status)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <a href={pdfUrl(inv.id)} target="_blank" rel="noreferrer" className="text-neutral-600 hover:text-neutral-900 mr-3" title="PDF" data-testid={`${docType}-pdf-${inv.id}`}><Download size={14} className="inline"/></a>
                    <button onClick={() => setEditing(inv)} className="text-neutral-600 hover:text-neutral-900 mr-3" title="Bewerken" data-testid={`${docType}-edit-${inv.id}`}><Edit3 size={14}/></button>
                    {inv.status === "draft" && (
                      <button onClick={() => send(inv.id)} className="text-emerald-700 hover:text-emerald-900 mr-3" title="Versturen" data-testid={`${docType}-send-${inv.id}`}><Send size={14}/></button>
                    )}
                    {docType === "invoice" && inv.status === "sent" && (
                      <button onClick={() => markPaid(inv.id)} className="text-emerald-700 hover:text-emerald-900 mr-3" title="Markeer als betaald" data-testid={`${docType}-markpaid-${inv.id}`}><Check size={14}/></button>
                    )}
                    {inv.mollie_checkout_url && (
                      <a href={inv.mollie_checkout_url} target="_blank" rel="noreferrer" className="text-blue-700 hover:text-blue-900 mr-3" title="Betaallink"><ExternalLink size={14} className="inline"/></a>
                    )}
                    <button onClick={() => remove(inv.id)} className="text-neutral-600 hover:text-rose-700" title="Verwijderen" data-testid={`${docType}-delete-${inv.id}`}><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <InvoiceDialog
          initial={editing}
          customers={customers}
          products={products}
          onClose={() => setEditing(null)}
          onSave={save}
          docType={docType}
          LabelCap={LabelCap}
        />
      )}
    </div>
  );
};

const InvoiceDialog = ({ initial, customers, products, onClose, onSave, docType, LabelCap }) => {
  const [inv, setInv] = useState({
    ...initial,
    lines: (initial.lines && initial.lines.length ? initial.lines : [{ description: "", quantity: 1, unit_price: 0, vat_rate: 21 }])
      .map((l) => ({ ...l })),
  });
  const set = (k, v) => setInv((p) => ({ ...p, [k]: v }));
  const setLine = (i, k, v) => setInv((p) => {
    const lines = p.lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l);
    return { ...p, lines };
  });
  const addLine = () => setInv((p) => ({ ...p, lines: [...p.lines, { description: "", quantity: 1, unit_price: 0, vat_rate: 21 }] }));
  const delLine = (i) => setInv((p) => ({ ...p, lines: p.lines.filter((_, idx) => idx !== i) }));

  const fillFromProduct = (i, productId) => {
    const prod = products.find((p) => p.id === productId);
    if (!prod) return;
    setLine(i, "description", prod.description ? `${prod.name} — ${prod.description}` : prod.name);
    setLine(i, "unit_price", prod.unit_price);
    setLine(i, "vat_rate", prod.vat_rate);
  };

  const subtotal = inv.lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
  const vat = inv.lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0) * (Number(l.vat_rate) || 0) / 100, 0);
  const total = subtotal + vat;

  const submit = (e) => {
    e.preventDefault();
    if (!inv.customer_id) { alert("Kies een klant"); return; }
    if (!inv.lines.length || inv.lines.every((l) => !l.description.trim())) { alert("Voeg minimaal één regel toe"); return; }
    const cleaned = {
      ...inv,
      lines: inv.lines
        .filter((l) => l.description.trim())
        .map((l) => ({
          description: l.description,
          quantity: Number(l.quantity) || 0,
          unit_price: Number(l.unit_price) || 0,
          vat_rate: Number(l.vat_rate) || 0,
        })),
    };
    onSave(cleaned);
  };

  return (
    <Modal onClose={onClose} title={inv.id ? `${LabelCap} ${inv.number || ""} bewerken` : `Nieuwe ${LabelCap.toLowerCase()}`} wide>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-neutral-600 uppercase tracking-wider">Klant</label>
            <select
              value={inv.customer_id} onChange={(e) => set("customer_id", e.target.value)}
              className="mt-1 w-full border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-900"
              data-testid="invoice-customer-select"
              required
            >
              <option value="">— Kies klant —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.company_name ? `${c.company_name} — ${c.name}` : c.name}</option>
              ))}
            </select>
          </div>
          <Field label="Datum" type="date" value={inv.date || ""} onChange={(v) => set("date", v)}/>
          <Field label={docType === "quote" ? "Geldig tot" : "Vervaldatum"} type="date" value={inv.due_date || ""} onChange={(v) => set("due_date", v)}/>
        </div>

        <div className="border border-neutral-200">
          <div className="grid grid-cols-[1fr_70px_110px_70px_110px_30px] gap-2 px-3 py-2 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
            <div>Omschrijving</div>
            <div className="text-right">Aantal</div>
            <div className="text-right">Prijs €</div>
            <div className="text-right">BTW%</div>
            <div className="text-right">Totaal</div>
            <div></div>
          </div>
          {inv.lines.map((l, i) => {
            const lineTotal = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
            return (
              <div key={i} className="grid grid-cols-[1fr_70px_110px_70px_110px_30px] gap-2 px-3 py-2 border-t border-neutral-100 items-start">
                <div>
                  <input
                    value={l.description}
                    onChange={(e) => setLine(i, "description", e.target.value)}
                    placeholder="Omschrijving"
                    className="w-full border border-neutral-300 px-2 py-1.5 text-sm outline-none focus:border-neutral-900"
                    data-testid={`line-desc-${i}`}
                  />
                  {products.length > 0 && (
                    <select
                      onChange={(e) => { if (e.target.value) { fillFromProduct(i, e.target.value); e.target.value = ""; } }}
                      className="mt-1 text-[11px] text-neutral-500 bg-transparent outline-none"
                      defaultValue=""
                    >
                      <option value="">+ gebruik product/dienst</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.name} — {fmtEur(p.unit_price)}</option>)}
                    </select>
                  )}
                </div>
                <input type="number" step="0.01" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} className="border border-neutral-300 px-2 py-1.5 text-sm text-right outline-none focus:border-neutral-900" data-testid={`line-qty-${i}`}/>
                <input type="number" step="0.01" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} className="border border-neutral-300 px-2 py-1.5 text-sm text-right outline-none focus:border-neutral-900" data-testid={`line-price-${i}`}/>
                <input type="number" step="0.01" value={l.vat_rate} onChange={(e) => setLine(i, "vat_rate", e.target.value)} className="border border-neutral-300 px-2 py-1.5 text-sm text-right outline-none focus:border-neutral-900"/>
                <div className="text-right text-sm py-1.5">{fmtEur(lineTotal)}</div>
                <button type="button" onClick={() => delLine(i)} className="text-neutral-400 hover:text-rose-700 py-1.5"><X size={14}/></button>
              </div>
            );
          })}
          <button type="button" onClick={addLine} className="text-sm text-neutral-600 hover:text-neutral-900 px-3 py-2 border-t border-neutral-100 w-full text-left" data-testid="line-add">
            + regel toevoegen
          </button>
        </div>

        <div className="flex justify-end">
          <div className="w-80 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-neutral-600">Subtotaal excl. BTW</span><span>{fmtEur(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-neutral-600">BTW</span><span>{fmtEur(vat)}</span></div>
            <div className="flex justify-between border-t pt-1 font-semibold"><span>Totaal</span><span>{fmtEur(total)}</span></div>
          </div>
        </div>

        <div>
          <label className="text-xs text-neutral-600 uppercase tracking-wider">Opmerkingen</label>
          <textarea value={inv.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1 w-full border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-900"/>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-neutral-600 hover:text-neutral-900">Annuleren</button>
          <button type="submit" className="px-5 py-2 bg-neutral-900 text-white text-sm font-medium hover:bg-black" data-testid="invoice-save">Opslaan</button>
        </div>
      </form>
    </Modal>
  );
};

// ===== Settings =====
const SettingsTab = ({ secret }) => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const load = async () => {
    const r = await axios.get(`${API}/admin/settings`, { params: { secret } });
    setS(r.data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  if (!s) return <Spinner/>;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, {
        invoice_prefix: s.invoice_prefix,
        next_invoice_number: parseInt(s.next_invoice_number) || 1,
        quote_prefix: s.quote_prefix,
        next_quote_number: parseInt(s.next_quote_number) || 1,
        payment_term_days: parseInt(s.payment_term_days) || 7,
      }, { params: { secret } });
      setSavedAt(new Date());
    } finally { setSaving(false); }
  };

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  return (
    <form onSubmit={save} className="max-w-xl space-y-5">
      <div>
        <h3 className="text-sm font-bold text-neutral-900 mb-3 uppercase tracking-wider">Factuurnummering</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Factuur-prefix" value={s.invoice_prefix || ""} onChange={(v) => set("invoice_prefix", v)} placeholder="2026"/>
          <Field label="Volgende factuurnummer" type="number" value={s.next_invoice_number} onChange={(v) => set("next_invoice_number", v)}/>
        </div>
        <p className="text-xs text-neutral-500 mt-1">Volgende factuur wordt <b>{s.invoice_prefix}{s.next_invoice_number}</b>.</p>
      </div>
      <div>
        <h3 className="text-sm font-bold text-neutral-900 mb-3 uppercase tracking-wider">Offertenummering</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Offerte-prefix" value={s.quote_prefix || ""} onChange={(v) => set("quote_prefix", v)} placeholder="OFF-2026-"/>
          <Field label="Volgende offertenummer" type="number" value={s.next_quote_number} onChange={(v) => set("next_quote_number", v)}/>
        </div>
      </div>
      <div>
        <h3 className="text-sm font-bold text-neutral-900 mb-3 uppercase tracking-wider">Overig</h3>
        <Field label="Betalingstermijn (dagen)" type="number" value={s.payment_term_days} onChange={(v) => set("payment_term_days", v)}/>
      </div>
      <div className="flex items-center gap-3 pt-2">
        <button type="submit" disabled={saving} className="px-5 py-2 bg-neutral-900 text-white text-sm font-medium hover:bg-black disabled:opacity-50" data-testid="settings-save">
          {saving ? "Opslaan..." : "Opslaan"}
        </button>
        {savedAt && <span className="text-sm text-emerald-700">Opgeslagen ✓</span>}
      </div>
    </form>
  );
};

// ===== helpers =====
const Field = ({ label, value, onChange, type = "text", required = false, placeholder = "", step, testId }) => (
  <div>
    <label className="text-xs text-neutral-600 uppercase tracking-wider">{label}{required && <span className="text-rose-600"> *</span>}</label>
    <input
      type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      required={required} placeholder={placeholder} step={step}
      data-testid={testId}
      className="mt-1 w-full border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-neutral-900"
    />
  </div>
);

const Spinner = () => (
  <div className="flex items-center gap-2 text-neutral-500 text-sm py-10 justify-center">
    <Loader2 size={14} className="animate-spin"/> Laden...
  </div>
);

const EmptyState = ({ text }) => (
  <div className="text-center py-14 border border-dashed border-neutral-200">
    <Euro size={24} className="mx-auto text-neutral-300 mb-2"/>
    <p className="text-sm text-neutral-500">{text}</p>
  </div>
);

const Modal = ({ title, onClose, children, wide = false }) => (
  <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center bg-black/50 p-4 overflow-y-auto">
    <div className={`bg-white w-full ${wide ? "max-w-4xl" : "max-w-2xl"} my-8`}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
        <h2 className="text-lg font-bold text-neutral-900">{title}</h2>
        <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900" data-testid="modal-close"><X size={18}/></button>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  </div>
);

export default AdminCRM;
